import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { SpinnerIcon } from "@/components/ui/loading"

export interface ConfirmDialogProps {
  open: boolean
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  /** "primary" → indigo button, "danger" → red button. Defaults to primary. */
  confirmVariant?: "primary" | "danger"
  /**
   * When set, the confirm button stays disabled until the user types this
   * string exactly. Reserved for destructions that cannot be undone AND cannot
   * be reconstructed — survey responses given by named raters about named
   * subjects. Using it everywhere would make it ritual.
   */
  requireText?: string
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "primary",
  requireText,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false)
  const [typed, setTyped] = useState("")
  const locked = requireText !== undefined && typed.trim() !== requireText

  async function handleConfirm() {
    setBusy(true)
    try {
      await onConfirm()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !busy) onCancel()
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {requireText !== undefined ? (
          <div className="space-y-1.5">
            <label className="text-caption" htmlFor="confirm-require-text">
              Type <span className="font-medium text-foreground">{requireText}</span> to confirm
            </label>
            <input
              id="confirm-require-text"
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-transparent px-3 text-sm outline-none focus:border-[var(--color-primary)]"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              disabled={busy}
            />
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant === "danger" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={busy || locked}
          >
            {busy ? <SpinnerIcon className="h-4 w-4" /> : null}
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
